// Reading the sales spreadsheet into leads (F-HQ-39, spec/14 §16): which column is which, and what each cell means.
// Pure: the server reads the bytes (CSV or XLSX) and hands rows of text in; the same function can preview in a console.
import { normalizeIndianPhone } from "./index.js";
/** Header aliases, compared lower-case with everything but letters and digits removed. */
const ALIASES = {
    businessName: ['venue', 'venuename', 'name', 'business', 'businessname', 'cafe', 'cafename', 'gamingcafe', 'shop', 'shopname'],
    contactName: ['contact', 'contactname', 'contactperson', 'owner', 'ownername', 'person'],
    phone: ['phone', 'phonenumber', 'mobile', 'mobilenumber', 'whatsapp', 'whatsappnumber', 'contactnumber', 'tel', 'telephone'],
    email: ['email', 'emailaddress', 'mail'],
    instagram: ['instagram', 'instagramhandle', 'insta', 'ig'],
    city: ['city', 'town'],
    area: ['area', 'locality', 'neighbourhood', 'neighborhood', 'suburb'],
    addressHint: ['addresshint', 'address'],
    stationCount: ['stations', 'stationcount', 'numberofstations', 'pcs', 'pccount', 'seats'],
    platforms: ['platforms', 'platform', 'consoles'],
    currentSoftware: ['currentsoftware', 'software', 'cafesoftware', 'currentsystem'],
    rating: ['googlerating', 'rating', 'stars'],
    reviews: ['reviews', 'reviewcount', 'googlereviews', 'numberofreviews', 'googlereviewcount'],
    mapUrl: ['mapslink', 'maplink', 'map', 'maps', 'googlemaps', 'mapurl'],
    hook: ['personalisationhook', 'personalizationhook', 'personalisation', 'personalization', 'hook'],
    priority: ['priority', 'prio'],
    notes: ['notes', 'note', 'comments', 'comment', 'remarks'],
};
const FIELD_BY_ALIAS = new Map(Object.entries(ALIASES).flatMap(([field, aliases]) => aliases.map((alias) => [alias, field])));
const squash = (header) => header.toLowerCase().replace(/[^a-z0-9]/g, '');
/** `@Handle`, `instagram.com/handle/?hl=en` or `handle` → `handle`; `null` when it is not one. */
export function normalizeInstagramHandle(input) {
    let value = input.trim();
    const url = /^(?:https?:\/\/)?(?:www\.)?instagram\.com\/([^/?#\s]+)/i.exec(value);
    if (url?.[1] !== undefined)
        value = url[1];
    value = value.replace(/^@+/, '').replace(/\/+$/, '');
    return /^[A-Za-z0-9._]{1,30}$/.test(value) ? value.toLowerCase() : null;
}
const clean = (value) => {
    const text = (value ?? '').trim();
    return text === '' ? null : text;
};
/** Decimal rating (`4.6`, `4,6`) → tenths (`46`); `null` when it is not a rating from 0 to 5. */
function ratingTenths(value) {
    if (value === null)
        return null;
    const match = /^(\d)(?:[.,](\d{1,2}))?$/.exec(value.trim());
    if (match?.[1] === undefined)
        return null;
    const tenths = Math.round(Number(`${match[1]}.${match[2] ?? '0'}`) * 10);
    return tenths >= 0 && tenths <= 50 ? tenths : null;
}
function wholeNumber(value) {
    if (value === null)
        return null;
    const digits = value.replace(/[,\s]/g, '');
    return /^\d{1,9}$/.test(digits) ? Number(digits) : null;
}
/** Read rows of text (header first, separately) as leads. A row with no business name, or a phone that cannot be read, is refused. */
export function parseLeadImport(headers, rows) {
    const columnOf = new Map();
    const matched = [];
    const ignored = [];
    headers.forEach((header, index) => {
        const label = header.trim();
        if (label === '')
            return;
        const field = FIELD_BY_ALIAS.get(squash(label));
        if (field === undefined || columnOf.has(field))
            ignored.push(label);
        else {
            columnOf.set(field, index);
            matched.push(label);
        }
    });
    const records = [];
    const refused = [];
    rows.forEach((cells, offset) => {
        const row = offset + 2;
        if (cells.every((cell) => cell.trim() === ''))
            return;
        const get = (field) => {
            const index = columnOf.get(field);
            return index === undefined ? null : clean(cells[index]);
        };
        const businessName = get('businessName');
        if (businessName === null) {
            refused.push({ row, reason: 'No business name.' });
            return;
        }
        if (businessName.length > 200) {
            refused.push({ row, reason: 'The business name is too long.' });
            return;
        }
        const phoneCell = get('phone');
        let phone = null;
        let phoneMobile = false;
        if (phoneCell !== null) {
            // A cell may hold several numbers ("98765 43210 / 020 4729 4519"): the first one that reads.
            const read = phoneCell
                .split(/[,;/\n]/)
                .map((part) => normalizeIndianPhone(part))
                .find((candidate) => candidate !== null);
            if (read === undefined || read === null) {
                refused.push({ row, reason: 'The phone number could not be read.' });
                return;
            }
            phone = read.e164;
            phoneMobile = read.mobile;
        }
        const instagramCell = get('instagram');
        const priorityCell = get('priority')?.toLowerCase();
        const mapCell = get('mapUrl');
        const email = get('email');
        const stationCount = wholeNumber(get('stationCount'));
        records.push({
            row,
            businessName,
            contactName: clip(get('contactName'), 120),
            phone,
            phoneMobile,
            email: email !== null && /^[^\s@]+@[^\s@]+$/.test(email) && email.length <= 254 ? email : null,
            instagram: instagramCell === null ? null : normalizeInstagramHandle(instagramCell),
            city: clip(get('city'), 80),
            area: clip(get('area'), 120),
            addressHint: clip(get('addressHint'), 300),
            stationCount: stationCount !== null && stationCount <= 1000 ? stationCount : null,
            platforms: (get('platforms') ?? '')
                .split(/[,;/]/)
                .map((tag) => tag.trim())
                .filter((tag) => tag !== '' && tag.length <= 40)
                .slice(0, 10),
            currentSoftware: clip(get('currentSoftware'), 120),
            googleRatingTenths: ratingTenths(get('rating')),
            googleReviewCount: wholeNumber(get('reviews')),
            mapUrl: mapCell !== null && /^https?:\/\/\S+$/i.test(mapCell) && mapCell.length <= 500 ? mapCell : null,
            hook: clip(get('hook'), 500),
            priority: priorityCell === 'a' || priorityCell === 'b' || priorityCell === 'c' ? priorityCell : 'c',
            notes: clip(get('notes'), 2000),
        });
    });
    return { records, refused, columns: { matched, ignored } };
}
function clip(value, max) {
    return value === null ? null : value.slice(0, max);
}
/** The key two rows are the same café by when neither has a phone: lower-case name and city. */
export function leadNameKey(businessName, city) {
    return `${businessName.trim().toLowerCase().replace(/\s+/g, ' ')}|${(city ?? '').trim().toLowerCase()}`;
}
//# sourceMappingURL=import.js.map